/**
 * Status derivation for the new-UX Timeline Items table + details panel, rendered through
 * StatusChip (newux/kit/controls.tsx) per the "Status chips" reference sheet.
 */
import {
  AutomationActionStatus,
  AutomationStatus,
  ConcreteExperienceMessage,
  IAutomationDenormalized,
  IExperience,
  ITaskDenormalized,
  MessageStatus,
  SubTaskStatus,
  SubTaskType,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { ErroDataType, InstanceError } from '@dadohr/shared/domainTypes/instance';
import moment from 'moment-timezone';
import { UnsafeTFunction } from '../../../../../i18next';
import {
  StatusChipTone,
  StatusChipVariant
} from '../../../components/Builders/newux/kit/controls';
import { SECOND_MESSAGE_STATUS_CUT_OFF } from './MessageDoneSendStatus';
import {
  fromMoment,
  Timestamp,
  toMoment,
  tsIsAfter
} from '../../../../../services/time';
import { ItemData } from '../../../../../services/participatDialog/participantDialogHelpers';
import { joinWithCommaAndAnd } from '../../../../../services/enumerate';
import { useRoutes } from '../../../../../routes';
import { getItemDisplayName } from '../../../helpers/adminName';
import {
  getMessageAtMillis,
  isErrorBeingRetried,
  isErrorSuperseded
} from '../../../../../services/experiences/concreteMessageAttempts';
import {
  getCooldownRemainingMs,
  getLastResendAtMillis,
  localRetryKey,
  toMillis
} from '../../../../../services/experiences/retryCooldown';

export type ChipStyle = { tone: StatusChipTone; variant?: StatusChipVariant };

/**
 * A task's TaskStatus enum has no ERROR value — like the classic Task.tsx, an errored task
 * is derived from its start messages' delivery status instead. Mirrors
 * TaskStatusWithConcreteMessagesCheck (Task.tsx:489-537), including the pre-migration
 * cutoff below which concrete messages aren't reliably present.
 */
export type TaskErrorState = 'ERROR' | 'IN_DELIVERY' | 'WRITE_FAILED' | null;

/** Issues meaning "a subtask answer failed to write to a data destination". */
const WRITE_FAILED_ERROR_TYPES: ReadonlySet<string> = new Set([
  ErroDataType.SALESFORCE_WRITE_FAILED,
  ErroDataType.UKG_WRITE_FAILED,
  ErroDataType.WORKDAY_WRITE_FAILED
]);

/** Subtask template ids with an open (not ignored/retried) data-destination write failure. */
export const getWriteFailedSubtaskTplIds = (
  instanceErrors: InstanceError[] | undefined
): ReadonlySet<string> =>
  new Set(
    (instanceErrors ?? [])
      .filter((e) => WRITE_FAILED_ERROR_TYPES.has(e.type) && !e.ignore && e.subtaskTplId)
      .map((e) => e.subtaskTplId)
  );

export const getTaskErrorState = (
  task: ITaskDenormalized,
  concreteMessagesByTask: { [taskId: string]: ConcreteExperienceMessage[] },
  nowMillis: number = Date.now(),
  writeFailedSubtaskTplIds?: ReadonlySet<string>
): TaskErrorState => {
  // A failed data-destination write outranks the task's own status: the
  // participant may be done, but their answers didn't reach the destination.
  if (
    task.status !== TaskStatus.SKIPPED &&
    writeFailedSubtaskTplIds?.size &&
    task.subTasks.some((s) => writeFailedSubtaskTplIds.has(s.templateId))
  ) {
    return 'WRITE_FAILED';
  }
  if (
    task.status === TaskStatus.COMPLETE ||
    task.status === TaskStatus.SKIPPED ||
    task.status === TaskStatus.NOT_STARTED
  ) {
    return null;
  }
  if (task.start.toMillis() < SECOND_MESSAGE_STATUS_CUT_OFF) {
    return null;
  }
  const startMessages = (concreteMessagesByTask[task.id] || []).filter(
    (m) =>
      m.type === 'TASK_START' ||
      m.type === 'TASK_START_PER_COHORT' ||
      m.type === 'EXPERIENCE_MESSAGE'
  );
  if (!startMessages.length) return 'IN_DELIVERY';
  if (startMessages.find((m) => m.status === 'OK')) return null;
  // Closes new-main-1cwf: a BOUNCED/ERROR message must surface as Error even when
  // another message is still QUEUED. SC-36146: …unless that failure was already
  // dealt with — see resolveFailedAttemptsState.
  const failed = startMessages.filter(isFailedConcreteMessage);
  if (failed.length) return resolveFailedAttemptsState(failed, startMessages, nowMillis);
  if (startMessages.find((m) => m.status === 'QUEUED')) return 'IN_DELIVERY';
  return 'ERROR';
};

const isFailedConcreteMessage = (m: ConcreteExperienceMessage) =>
  m.status === 'BOUNCED' || m.status === 'ERROR';

/**
 * SC-36146 — a resend appends a NEW concrete message rather than updating the
 * failed one, so "any row failed ⇒ Error" kept a successfully retried item in
 * Error forever. A failure only counts while it is still open: not ignored
 * (issues-bar Retry/Ignore), not superseded by a later delivered attempt on the
 * SAME channel, and not covered by a retry still in flight on that channel
 * (then the item is IN_DELIVERY — "retry in progress"). Per-channel on purpose:
 * a failed Slack send stays an Error even when email went through.
 */
const resolveFailedAttemptsState = (
  failed: ConcreteExperienceMessage[],
  all: ConcreteExperienceMessage[],
  nowMillis: number
): TaskErrorState => {
  const retrying = failed.filter((m) => isErrorBeingRetried(m, all, nowMillis));
  const open = failed.filter(
    (m) => !m.ignore && !isErrorSuperseded(m, all) && !retrying.includes(m)
  );
  if (open.length) return 'ERROR';
  return retrying.length ? 'IN_DELIVERY' : null;
};

/**
 * Failures are judged per channel attempt (see resolveFailedAttemptsState). With no
 * failure, mirrors the classic MessageDoneSendStatus: a DONE message with no OK record
 * on any channel (none at all, or only QUEUED/UNKNOWN) is still in delivery.
 */
export const getMessageErrorState = (
  msgId: string,
  status: MessageStatus,
  concreteMessagesByMessage: { [msgId: string]: ConcreteExperienceMessage[] },
  nowMillis: number = Date.now()
): TaskErrorState => {
  if (status !== MessageStatus.DONE) return null;
  const messages = concreteMessagesByMessage[msgId] || [];
  const failed = messages.filter(isFailedConcreteMessage);
  if (failed.length) return resolveFailedAttemptsState(failed, messages, nowMillis);
  if (!messages.some((m) => m.status === 'OK')) return 'IN_DELIVERY';
  return null;
};

/**
 * The specific concrete message an errored Task/Message item's status is derived from —
 * same selection as getTaskErrorState's Task branch, and MessageDoneSendStatus's Message
 * branch — so it can be fed into the SENT_ITEM issue copy for inline error text.
 * Automations resolve their error text per-action instead (see
 * getActionErrorDetailsText in ParticipantDialog.tsx), not through this.
 */
export const getItemErrorConcreteMessage = (
  item: ItemData,
  concreteMessagesByTask: { [taskId: string]: ConcreteExperienceMessage[] },
  concreteMessagesByMessage: { [msgId: string]: ConcreteExperienceMessage[] }
): ConcreteExperienceMessage | null => {
  if (item.type === 'TASK') {
    const startMessages = (concreteMessagesByTask[item.data.id] || []).filter(
      (m) =>
        m.type === 'TASK_START' ||
        m.type === 'TASK_START_PER_COHORT' ||
        m.type === 'EXPERIENCE_MESSAGE'
    );
    return startMessages.find((m) => m.status === 'BOUNCED' || m.status === 'ERROR') || null;
  }
  if (item.type === 'MESSAGE') {
    // Newest failure still open — same per-channel attempt rules as getMessageErrorState.
    const messages = concreteMessagesByMessage[item.data.templateId] || [];
    return messages
      .filter((m) => isFailedConcreteMessage(m) && !m.ignore && !isErrorSuperseded(m, messages))
      .reduce<ConcreteExperienceMessage | null>(
        (newest, m) =>
          !newest || getMessageAtMillis(m) >= getMessageAtMillis(newest) ? m : newest,
        null
      );
  }
  return null;
};

/**
 * Sort key / single-date fallback for the table's date column. Tasks are date RANGES
 * (see getItemDateRange) — this returns the task's start for sort-ordering purposes,
 * not what's actually displayed in the task row.
 */
export const getItemDate = (item: ItemData): Timestamp | null => {
  if (item.type === 'TASK') {
    return item.data.start;
  }
  if (item.type === 'MESSAGE') {
    return item.data.sendAt;
  }
  // AUTOMATION
  if (item.data.completedAt) return item.data.completedAt;
  if (item.data.timing.type === 'ABSOLUTE') return item.data.timing.start;
  return null;
};

/**
 * The originally-scheduled date, for comparison against getItemActualDate below.
 * Unlike getItemDate, this never substitutes completedAt for an automation — an
 * automation's scheduled trigger time and its completion time are different things,
 * and comparing completedAt against itself would always read as "on time".
 */
export const getItemScheduledDate = (item: ItemData): Timestamp | null => {
  if (item.type === 'TASK') return item.data.start;
  if (item.type === 'MESSAGE') return item.data.sendAt;
  // AUTOMATION
  return item.data.timing.type === 'ABSOLUTE' ? item.data.timing.start : null;
};

/**
 * Tasks display as a start–deadline range in the table (e.g. "24 Jun–28 Jun")
 * Messages/Automations are single-point-in-time and have no range
 */
export const getItemDateRange = (
  item: ItemData
): { start: Timestamp; end: Timestamp } | null => {
  if (item.type !== 'TASK') return null;
  return { start: item.data.start, end: item.data.end };
};

/**
 * The actual start/send/trigger time on record for an item, distinct from the
 * scheduled time getItemDate returns. Tasks/Messages derive this from their
 * concrete messages (mirrors old UX's TaskStartDateWithConcreteMessagesCheck in
 * Task.tsx and MessageSendDate.tsx). Automations have no "attempted/errored at"
 * timestamp on the record today, so completedAt is used as a proxy — this only
 * reflects a late-but-eventually-successful run, not a still-erroring one.
 */
export const getItemActualDate = (
  item: ItemData,
  concreteMessagesByTask: { [taskId: string]: ConcreteExperienceMessage[] },
  concreteMessagesByMessage: { [msgId: string]: ConcreteExperienceMessage[] }
): Timestamp | null => {
  if (item.type === 'TASK') {
    const startMessages = (concreteMessagesByTask[item.data.id] || [])
      .filter(
        (m) =>
          m.type === 'TASK_START' ||
          m.type === 'TASK_START_PER_COHORT' ||
          m.type === 'EXPERIENCE_MESSAGE'
      )
      .sort((a, b) => a.at.toMillis() - b.at.toMillis());
    return startMessages[0]?.at || null;
  }
  if (item.type === 'MESSAGE') {
    const messages = (concreteMessagesByMessage[item.data.templateId] || [])
      .slice()
      .sort((a, b) => a.at.toMillis() - b.at.toMillis());
    return messages[messages.length - 1]?.at || null;
  }
  // AUTOMATION
  return item.data.completedAt || null;
};

/**
 * Whether an item's actual date differs from its scheduled date by more than a
 * day — mirrors old UX Task.tsx's same-day comparison for "should have started".
 */
export const isItemLate = (
  scheduled: Timestamp | null,
  actual: Timestamp | null
): boolean => {
  if (!scheduled || !actual) return false;
  return !toMoment(scheduled).isSame(toMoment(actual), 'day');
};

/**
 * "Start today" (Task) / "Send today" (Message) / "Run today" (Automation) eligibility: —
 * Task.tsx's isTaskAfterToday requires the start be at least 2 days out
 * Message.tsx/Automation.tsx's isInFuture allows any day after today
 * Only applies to items that haven't started yet: a fixed-date item can be moved to today
 * (stages locally via setD, needs Save); a trigger-based item has no date to move, so it's
 * force-triggered now instead.
 */
export type StartTodayEligibility = 'FIXED_DATE' | 'TRIGGERED' | null;

export const getStartTodayEligibility = (
  item: ItemData,
  category: StatusCategory
): StartTodayEligibility => {
  if (category !== 'UPCOMING') return null;
  if (item.triggeredBy?.length) return 'TRIGGERED';
  const date = getItemDate(item);
  if (!date) return null;
  if (item.type === 'TASK') {
    return tsIsAfter(date, fromMoment(moment().startOf('day').add(1, 'days')))
      ? 'FIXED_DATE'
      : null;
  }
  return moment.unix(date.seconds).diff(moment(), 'days') > 0 ? 'FIXED_DATE' : null;
};

export type StatusCategory =
  | 'DONE'
  | 'ERROR'
  | 'SKIPPED'
  | 'UPCOMING'
  /**
   * In progress, not yet done: a Task that is RUNNING/LATE/PAST_DEADLINE, or an
   * Automation with an in-flight sync action (display status 'RUNNING').
   */
  | 'ACTIONABLE';

/**
 * An automation's persisted status is the backend's rollup of its action statuses
 * (computeAutomationStatus), written when an action completes. That rollup can be stale —
 * an automation can still read ERROR after every errored action has been retried to DONE.
 * Rather than trust it, the new UX derives the terminal states from the actions here,
 * using the same rules as the backend, so the automation's chip never contradicts the
 * action list shown beneath it. Non-terminal states fall back to the denormalizer's
 * display status so the synthetic 'RUNNING' (in-flight sync) survives.
 */
export const getAutomationDisplayStatus = (
  a: IAutomationDenormalized
): AutomationStatus | 'RUNNING' => {
  const statuses = a.actions.map((ac) => ac.status);
  if (!statuses.length) return a.status;
  if (statuses.some((s) => s === AutomationActionStatus.ERROR)) return AutomationStatus.ERROR;
  if (statuses.every((s) => s === AutomationActionStatus.SKIPPED)) return AutomationStatus.SKIPPED;
  if (
    statuses.every(
      (s) => s === AutomationActionStatus.DONE || s === AutomationActionStatus.SKIPPED
    )
  ) {
    return AutomationStatus.DONE;
  }
  return a.status;
};

export const getItemStatusChipConfig = (
  item: ItemData,
  t: UnsafeTFunction,
  taskErrorState: TaskErrorState = null,
  messageErrorState: TaskErrorState = null
): [string, ChipStyle] => {
  if (item.type === 'TASK') {
    if (taskErrorState === 'ERROR' || taskErrorState === 'WRITE_FAILED')
      return [t('Error'), { tone: 'red', variant: 'dark' }];
    if (taskErrorState === 'IN_DELIVERY') return [t('In delivery'), { tone: 'grey', variant: 'light' }];
    switch (item.data.status) {
      case TaskStatus.NOT_STARTED:
        return [t('Upcoming'), { tone: 'grey', variant: 'light' }];
      case TaskStatus.RUNNING:
        return [t('To Do'), { tone: 'blue', variant: 'light' }];
      case TaskStatus.LATE:
        return [t('Overdue'), { tone: 'red', variant: 'light' }];
      case TaskStatus.COMPLETE:
        return [t('Done'), { tone: 'green', variant: 'light' }];
      case TaskStatus.SKIPPED:
        return [t('Skipped'), { tone: 'grey', variant: 'dark' }];
      case TaskStatus.PAST_DEADLINE:
        return [t('Past Deadline'), { tone: 'grey', variant: 'light' }];
      default:
        return [t('Upcoming'), { tone: 'grey', variant: 'light' }];
    }
  }
  if (item.type === 'MESSAGE') {
    if (messageErrorState === 'ERROR') return [t('Error'), { tone: 'red', variant: 'dark' }];
    if (messageErrorState === 'IN_DELIVERY') {
      return [t('In delivery'), { tone: 'grey', variant: 'light' }];
    }
    switch (item.data.status) {
      case MessageStatus.UPCOMING:
        return [t('Upcoming'), { tone: 'grey', variant: 'light' }];
      case MessageStatus.TRIGGERED:
        return [t('Triggered'), { tone: 'grey', variant: 'light' }];
      case MessageStatus.DONE:
        return [t('Sent'), { tone: 'green', variant: 'light' }];
      case MessageStatus.ERROR:
        return [t('Error'), { tone: 'red', variant: 'dark' }];
      case MessageStatus.SKIPPED:
        return [t('Skipped'), { tone: 'grey', variant: 'dark' }];
      default:
        return [t('Upcoming'), { tone: 'grey', variant: 'light' }];
    }
  }
  // AUTOMATION
  switch (getAutomationDisplayStatus(item.data)) {
    case AutomationStatus.UPCOMING:
      return [t('Upcoming'), { tone: 'grey', variant: 'light' }];
    case AutomationStatus.TRIGGERED:
      return [t('Triggered'), { tone: 'grey', variant: 'light' }];
    case 'RUNNING':
      return [t('Running'), { tone: 'blue', variant: 'light' }];
    case AutomationStatus.DONE:
      return [t('Done'), { tone: 'green', variant: 'light' }];
    case AutomationStatus.ERROR:
      return [t('Error'), { tone: 'red', variant: 'dark' }];
    case AutomationStatus.SKIPPED:
      return [t('Skipped'), { tone: 'grey', variant: 'dark' }];
    default:
      return [t('Upcoming'), { tone: 'grey', variant: 'light' }];
  }
};

export const getItemStatusCategory = (
  item: ItemData,
  taskErrorState: TaskErrorState = null,
  messageErrorState: TaskErrorState = null
): StatusCategory => {
  if (item.type === 'TASK') {
    if (taskErrorState === 'ERROR' || taskErrorState === 'WRITE_FAILED') return 'ERROR';
    switch (item.data.status) {
      case TaskStatus.COMPLETE:
        return 'DONE';
      case TaskStatus.SKIPPED:
        return 'SKIPPED';
      case TaskStatus.NOT_STARTED:
        return 'UPCOMING';
      case TaskStatus.RUNNING:
      case TaskStatus.LATE:
      case TaskStatus.PAST_DEADLINE:
        return 'ACTIONABLE';
      default:
        return 'UPCOMING';
    }
  }
  if (item.type === 'MESSAGE') {
    if (messageErrorState === 'ERROR') return 'ERROR';
    switch (item.data.status) {
      case MessageStatus.DONE:
        return 'DONE';
      case MessageStatus.SKIPPED:
        return 'SKIPPED';
      case MessageStatus.ERROR:
        return 'ERROR';
      default:
        return 'UPCOMING';
    }
  }
  // AUTOMATION
  switch (getAutomationDisplayStatus(item.data)) {
    case AutomationStatus.DONE:
      return 'DONE';
    case AutomationStatus.SKIPPED:
      return 'SKIPPED';
    case AutomationStatus.ERROR:
      return 'ERROR';
    case 'RUNNING':
      return 'ACTIONABLE';
    default:
      return 'UPCOMING';
  }
};

/** Task/Message resend eligibility: any status except UPCOMING or SKIPPED. Automations are never resendable. */
export const canResendItem = (item: ItemData, taskErrorState: TaskErrorState = null): boolean => {
  if (item.type === 'AUTOMATION') return false;
  const cat = getItemStatusCategory(item, taskErrorState);
  return cat !== 'UPCOMING' && cat !== 'SKIPPED';
};

/** True for a task with a DocuSign envelope subtask — resending it won't resend the Docusign notification itself. */
export const hasDocusignSubtask = (item: ItemData): boolean =>
  item.type === 'TASK' &&
  item.data.subTasks.some((s) => s.type === SubTaskType.DOCUSIGN_ENVELOPE_SIGN);

/**
 * "When task completes" items are categorized as UPCOMING, but can't actually be skipped
 * (yet – feature might be added in future) but the button shouldn't be shown because it 
 * doesn't currently work.
 * Not needed for "When conditions met" or "When data changes" since they don't appear in 
 * the list until triggered.
 */
export const isSkippableCategory = (item: ItemData, category: StatusCategory): boolean =>
  !item.triggeredBy?.length &&
  // ACTIONABLE for an automation means 'RUNNING' (an in-flight sync), which skipping
  // can't cancel — classic Automation.tsx offers no actions at all for it.
  !(item.type === 'AUTOMATION' && category === 'ACTIONABLE') &&
  (category === 'UPCOMING' || category === 'ERROR' || category === 'ACTIONABLE');

/** The subset of a multi-select that "Skip all" should actually act on — same rule as isSkippableCategory. */
export const getSkippableItems = (items: ItemData[], categories: StatusCategory[]): ItemData[] =>
  items.filter((i, idx) => isSkippableCategory(i, categories[idx]));

export const canSkipItem = (
  item: ItemData,
  taskErrorState: TaskErrorState = null,
  messageErrorState: TaskErrorState = null
): boolean =>
  isSkippableCategory(item, getItemStatusCategory(item, taskErrorState, messageErrorState));

export const canUnskipItem = (item: ItemData): boolean =>
  getItemStatusCategory(item) === 'SKIPPED';

export const canUncompleteItem = (item: ItemData): boolean =>
  getItemStatusCategory(item) === 'DONE';

export const isErrorItem = (
  item: ItemData,
  taskErrorState: TaskErrorState = null,
  messageErrorState: TaskErrorState = null
): boolean =>
  getItemStatusCategory(item, taskErrorState, messageErrorState) === 'ERROR';

export type MultiSelectActionEligibility = {
  anyDone: boolean;
  anyError: boolean;
  anySkipped: boolean;
  anySkippable: boolean;
  resendableItems: Extract<ItemData, { type: 'TASK' | 'MESSAGE' }>[];
  anyUpcomingResendable: boolean;
};

/**
 * Which bulk actions the multi-select details panel should offer for a set of selected
 * items, given each item's already-derived StatusCategory (same order as `items`).
 */
export const getMultiSelectActionEligibility = (
  items: ItemData[],
  categories: StatusCategory[]
): MultiSelectActionEligibility => {
  const anyDone = items.some((i, idx) => i.type === 'TASK' && categories[idx] === 'DONE');
  // Tasks have no Retry action (classic offers only Resend for an errored task) — only
  // Message/Automation errors make "Retry all errors" meaningful.
  const anyError = items.some((i, idx) => i.type !== 'TASK' && categories[idx] === 'ERROR');
  const anySkipped = categories.includes('SKIPPED');
  const anySkippable = items.some((i, idx) => isSkippableCategory(i, categories[idx]));
  const resendableItems = items.filter(
    (i, idx) =>
      i.type !== 'AUTOMATION' && categories[idx] !== 'UPCOMING' && categories[idx] !== 'SKIPPED'
  ) as Extract<ItemData, { type: 'TASK' | 'MESSAGE' }>[];
  const anyUpcomingResendable = items.some(
    (i, idx) => i.type !== 'AUTOMATION' && categories[idx] === 'UPCOMING'
  );

  return { anyDone, anyError, anySkipped, anySkippable, resendableItems, anyUpcomingResendable };
};

/**
 * Subtask/automation-action "Pending" status is no longer a flat label — it mirrors the
 * parent task's own status (Upcoming vs To do), or is Error if the parent task itself is
 * errored (an errored task's not-yet-done subtasks are errored too — none of them will
 * actually run until the parent's delivery issue is resolved).
 */
export const getSubTaskDisplayStatus = (
  subTaskStatus: SubTaskStatus,
  parentTaskCategory: StatusCategory,
  t: UnsafeTFunction
): [string, ChipStyle] => {
  if (subTaskStatus === SubTaskStatus.DONE) return [t('Done'), { tone: 'green', variant: 'light' }];
  if (subTaskStatus === SubTaskStatus.SKIPPED) return [t('Skipped'), { tone: 'grey', variant: 'dark' }];
  // OPEN
  if (parentTaskCategory === 'ERROR') return [t('Error'), { tone: 'red', variant: 'dark' }];
  if (parentTaskCategory === 'ACTIONABLE') return [t('To do'), { tone: 'blue', variant: 'light' }];
  return [t('Upcoming'), { tone: 'grey', variant: 'light' }];
};

export const getAutomationActionDisplayStatus = (
  status: AutomationActionStatus | 'RUNNING',
  t: UnsafeTFunction
): [string, ChipStyle] => {
  if (status === AutomationActionStatus.DONE) return [t('Done'), { tone: 'green', variant: 'light' }];
  if (status === AutomationActionStatus.SKIPPED) return [t('Skipped'), { tone: 'grey', variant: 'dark' }];
  if (status === AutomationActionStatus.ERROR) return [t('Error'), { tone: 'red', variant: 'dark' }];
  if (status === 'RUNNING') return [t('Running'), { tone: 'blue', variant: 'light' }];
  return [t('Upcoming'), { tone: 'grey', variant: 'light' }];
};

/**
 * Trigger-chain description for an item whose start is derived from another task
 * completing (item.triggeredBy — populated by _mapFn/participatDialogHelpers.ts for the
 * 'triggered' template timing type). Returns null for items with a fixed schedule.
 *
 * Data-change-triggered items ("Starts when Manager changes", "...conditions
 * met") are NOT covered here — those items have no instance record at all until their
 * watched field/rule actually matches (confirmed against the backend's
 * mutableAddDataChangeItems/evaluateDataChangeForUser), so they never reach ItemData in
 * the first place. 
 */
export const getTriggerDescription = (
  item: ItemData,
  t: UnsafeTFunction
): { short: string; verb: string; parentNames: string; full: string } | null => {
  if (!item.triggeredBy?.length) return null;

  const short =
    item.type === 'MESSAGE'
      ? t('Sends when…')
      : item.type === 'AUTOMATION'
      ? t('Runs when…')
      : t('Starts when…');

  const parentNames = joinWithCommaAndAnd(
    item.triggeredBy.map((p) => getItemDisplayName(p)),
    t
  );

  // `verb` is the bolded lead-in (e.g. "Sends when task completes") — kept separate from
  // `parentNames` so callers can style them differently (see TimelineItemDetailsPanel's
  // TriggerLine). i18next treats ':' inside a t() key as a namespace separator, so the
  // colon in `full` is appended outside the translated text.
  const verb =
    item.type === 'MESSAGE'
      ? t('Sends when task completes')
      : item.type === 'AUTOMATION'
      ? t('Runs when task completes')
      : t('Starts when task completes');
  const full = `${verb}: ${parentNames}`;

  return { short, verb, parentNames, full };
};

export const getVisibleAudienceName = (item: ItemData): string | null => {
  if (!item.audience || item.audience.rule === 'EVERYONE' || !item.audience.name) return null;
  return item.audience.name;
};

export const getTriggerTimelineItemUrl = (
  ROUTES: ReturnType<typeof useRoutes>['ROUTES'],
  tplId: string,
  item: ItemData
): string | null => {
  const parentTaskId = item.triggeredBy?.[0]?.templateId;
  if (!parentTaskId) return null;

  return ROUTES.experiences.details.general.url(tplId, {
    type: 'task',
    taskId: parentTaskId
  });
};

export const getTemplateItemEditUrl = (
  ROUTES: ReturnType<typeof useRoutes>['ROUTES'],
  tplId: string,
  item: ItemData
): string => {
  if (item.type === 'TASK') {
    return ROUTES.experiences.details.edit.url(tplId, {
      step: 'tasks',
      taskId: item.data.templateId
    });
  }
  if (item.type === 'MESSAGE') {
    return ROUTES.experiences.details.edit.url(tplId, {
      step: 'tasks',
      messageId: item.data.templateId
    });
  }
  return ROUTES.experiences.details.edit.url(tplId, {
    step: 'tasks',
    automationId: item.data.templateId
  });
};

/** Per-browser retry-mark key for a timeline item (used when the BE records no event). */
export const getItemLocalRetryKey = (orgId: string, instanceId: string, item: ItemData) =>
  localRetryKey(orgId, instanceId, `ITEM:${item.type}:${item.data.templateId}`);

/**
 * SC-36146 — ms until the item's Retry may be used again (0 = allowed). Messages and
 * tasks read the BE resend events (MESSAGE_RESENT / TASK_RESENT, shared by every admin);
 * automations, whose force-run records no event, use the per-browser mark. A failure
 * recorded after the retry means its outcome is known, so Retry is allowed again.
 */
export const getItemRetryCooldownMs = (
  item: ItemData,
  instance: { id: string; data: Pick<IExperience, 'orgId' | 'events'> },
  errorMessage: ConcreteExperienceMessage | null,
  nowMillis: number,
  localMarks: Record<string, number>
): number => {
  if (item.type === 'AUTOMATION') {
    const failedAt = Math.max(
      0,
      ...item.data.actions
        .filter((a) => a.status === AutomationActionStatus.ERROR)
        .map((a) => toMillis((a as { doneAt?: unknown }).doneAt))
    );
    return getCooldownRemainingMs(
      localMarks[getItemLocalRetryKey(instance.data.orgId, instance.id, item)] ?? null,
      nowMillis,
      failedAt || null
    );
  }
  const queuedAt = getLastResendAtMillis(
    instance.data.events,
    item.type === 'MESSAGE'
      ? { itemType: 'MESSAGE', msgTplId: item.data.templateId }
      : { itemType: 'TASK', taskTplIds: [item.data.templateId] }
  );
  return getCooldownRemainingMs(
    queuedAt,
    nowMillis,
    errorMessage ? getMessageAtMillis(errorMessage) || null : null
  );
};
